import {
  CHAT_INPUT_MODES,
  ChatAPI,
  resetCurrentChat,
  setChatLoading,
  setCurrentChatSession,
} from '@guuru/chat-web';
import events from '@guuru/events-web';
import log from '@guuru/logger-web';
import { parse, stringify } from '@guuru/url-common';
import buildChatSteps from './commands/buildChatSteps';
import createChat from './commands/createChat';
import handleChatStatusChange from './commands/handleChatStatusChange';
import submitChatMessage from './commands/submitChatMessage';
import runNextStep from './steps';

let runningStep;
let initializingChat;

const runUserInitiatedStep = async (options) => {
  if (runningStep) return;

  runningStep = runNextStep(options);

  try {
    await runningStep;
  } finally {
    runningStep = null;
  }
};

const initializeChat = async ({
  partnerId,
  chatId,
  chatControlToken,
  navigate,
  location,
  options,
}) => {
  setChatLoading(true);

  const action = {
    partnerId,
    chatId,
    chatControlToken,
    navigate,
    location,
    options,
  };

  await buildChatSteps(action);
  await runNextStep({ action });
};

const initChatFlow = (options) => {
  if (initializingChat) return initializingChat;

  initializingChat = initializeChat(options).finally(() => {
    initializingChat = null;
  });
  return initializingChat;
};

const submitChatText = async (partnerId, chatId, payload) => {
  const flowParams = { partnerId, chatId, ...payload };

  switch (payload.inputMode) {
    case CHAT_INPUT_MODES.question:
    case CHAT_INPUT_MODES.name:
    case CHAT_INPUT_MODES.email:
      flowParams.processArgs = {
        text: payload.text,
        skipAutomation: payload.skipAutomation,
      };
      break;
    case CHAT_INPUT_MODES.chat:
      await submitChatMessage(flowParams);
      return;
    default:
      throw new Error(
        `Unexpected chat input mode caught: ${payload.inputMode}`,
      );
  }

  await runUserInitiatedStep(flowParams);
};

const startNewQuestion = async (partnerId, navigate, location, options) => {
  if (initializingChat) return;

  try {
    events.newChatClick();

    const parameters = parse(location.search);
    delete parameters.payloadQuestion;
    ChatAPI.storeParams(parameters);
    const channel = options?.channel || 'chat';
    const chatUrl = ChatAPI.getChatURL(partnerId, channel, {
      qs: `?${stringify(parameters)}`,
    });
    navigate(chatUrl, { replace: true });

    ChatAPI.storeReset(partnerId);
    resetCurrentChat();

    await initChatFlow({
      partnerId,
      chatId: null,
      chatControlToken: null,
      navigate,
      location,
      options,
    });
  } catch (error) {
    log.error(error);
  }
};

const recoverChatConnection = (() => {
  let isRecoveringConnection = false;

  return async (partnerId) => {
    if (isRecoveringConnection) return;

    isRecoveringConnection = true;

    try {
      const { chatId, updatedChat } = await createChat(partnerId);

      if (!chatId) return;

      setCurrentChatSession({
        chatId,
        messages: updatedChat?.messages?.edges,
      });
      handleChatStatusChange({ chatId, ...updatedChat });
    } catch (error) {
      log.error(error);
    } finally {
      isRecoveringConnection = false;
    }
  };
})();

const runNextChatStep = (processArgs) => runUserInitiatedStep({ processArgs });

export {
  initChatFlow,
  recoverChatConnection,
  runNextChatStep,
  startNewQuestion,
  submitChatText,
};
